import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import {
  Alert,
  Collapse,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import UsStatesSelector from "components/common/UsStatesSelector";
import {
  DEFAULT_CONFIG,
  DefinitionsTooltip,
  VEHICLE_USAGE_DEFINITIONS,
  formatPptEquivalent,
} from "components/inputs/autoExposureRating/AutoExposureRating";
import useDerivedAutoHazardGrade from "components/inputs/autoExposureRating/useDerivedAutoHazardGrade";
import AutoPricingResults from "components/pricing/autoPricing/AutoPricingResults";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as analyticsService from "services/analyticsService";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import * as submissionSelector from "store/selectors/submission/submissionSelector";

const ENGINE = "united_es_auto_v1";

const FONT_SIZE = "0.875rem";

const TF = {
  variant: "filled",
  size: "small",
  fullWidth: true,
  InputLabelProps: { shrink: true },
};

const SectionBar = ({ label, summary, open, onToggle }) => (
  <Stack
    direction={"row"}
    alignItems={"center"}
    justifyContent={"space-between"}
    onClick={onToggle}
    sx={{
      backgroundColor: "grey.100",
      cursor: "pointer",
      px: 2,
      py: 1.25,
      borderRadius: 1,
    }}
  >
    <Typography fontSize={FONT_SIZE} fontWeight={700} color={"text.secondary"}>
      {label}
    </Typography>
    <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
      {summary != null && (
        <Typography
          fontSize={FONT_SIZE}
          fontWeight={600}
          color={"text.secondary"}
        >
          {summary}
        </Typography>
      )}
      {open ? (
        <ExpandLessIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      ) : (
        <ExpandMoreIcon sx={{ fontSize: 18, color: "text.secondary" }} />
      )}
    </Stack>
  </Stack>
);

const SubSectionTitle = ({ children }) => (
  <Typography
    variant={"overline"}
    color={"text.secondary"}
    sx={{ letterSpacing: 1, lineHeight: 1, fontSize: "0.7rem" }}
  >
    {children}
  </Typography>
);

const DropdownField = ({ label, value, onChange, options, definitions }) => (
  <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
    <FormControl fullWidth size={"small"} variant={"filled"}>
      <InputLabel shrink>{label}</InputLabel>
      <Select
        value={value ?? ""}
        label={label}
        displayEmpty
        onChange={(e) => onChange(e.target.value || null)}
      >
        <MenuItem value={""}>
          <em>{"(none)"}</em>
        </MenuItem>
        {options.map((opt) => (
          <MenuItem key={opt} value={opt}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
    {definitions && <DefinitionsTooltip definitions={definitions} />}
  </Stack>
);

const ExposureRatingInputs = ({ data, config, onUpdate }) => {
  const [open, setOpen] = useState(false);
  // Auto hazard grade, derived live by the engine (owned fleet + state + usage +
  // setting) — not recomputed in the frontend.
  const derivedHazardGrade = useDerivedAutoHazardGrade(data);

  const vehicleUsageOptions =
    config?.vehicleUsageOptions ?? DEFAULT_CONFIG.vehicleUsageOptions;
  const settingOptions =
    config?.settingOptions ?? DEFAULT_CONFIG.settingOptions;

  const filledCount = [
    data?.predominantState,
    data?.vehicleUsage,
    data?.setting,
    data?.hnoaEmployees,
    data?.hnoaAnnualCostOfHire,
    data?.primaryAutoMarketChargedPremium,
    data?.primaryAutoMarketLimit,
  ].filter((v) => v != null && v !== "").length;

  return (
    <Stack spacing={0}>
      <SectionBar
        label={"Exposure Rating Inputs"}
        summary={filledCount > 0 ? `${filledCount} set` : null}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      />
      <Collapse in={open} timeout={"auto"} unmountOnExit>
        <Stack spacing={2} sx={{ pt: 2, pb: 1, px: 1 }}>
          <Stack spacing={0.75}>
            <SubSectionTitle>{"Rating Drivers"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <UsStatesSelector
                  label={"Predominant State"}
                  stateId={data?.predominantState}
                  onChange={(v) => onUpdate("predominantState", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <DropdownField
                  label={"Vehicle Usage"}
                  value={data?.vehicleUsage}
                  onChange={(v) => onUpdate("vehicleUsage", v)}
                  options={vehicleUsageOptions}
                  definitions={VEHICLE_USAGE_DEFINITIONS}
                />
              </Grid>
              <Grid item xs={12}>
                <DropdownField
                  label={"Setting"}
                  value={data?.setting}
                  onChange={(v) => onUpdate("setting", v)}
                  options={settingOptions}
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Hired and Non-Owned Auto"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PrettyNumberTextField
                  {...TF}
                  label={"No. of Employees"}
                  value={data?.hnoaEmployees ?? null}
                  onChangeNumber={(v) => onUpdate("hnoaEmployees", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Annual Cost of Hire ($)"}
                  value={data?.hnoaAnnualCostOfHire ?? null}
                  onChangeNumber={(v) => onUpdate("hnoaAnnualCostOfHire", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  {...TF}
                  variant={"outlined"}
                  label={"PPT Equivalent (derived)"}
                  InputProps={{ readOnly: true }}
                  value={formatPptEquivalent(
                    data?.hnoaEmployees,
                    data?.hnoaAnnualCostOfHire
                  )}
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Auto Hazard Grade"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  {...TF}
                  variant={"outlined"}
                  label={"Auto Hazard Grade (derived)"}
                  InputProps={{ readOnly: true }}
                  value={derivedHazardGrade ?? "—"}
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack spacing={0.75}>
            <SubSectionTitle>{"Premium"}</SubSectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Primary Auto Market Charged Premium ($)"}
                  value={data?.primaryAutoMarketChargedPremium ?? null}
                  onChangeNumber={(v) =>
                    onUpdate("primaryAutoMarketChargedPremium", v)
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <PrettyNumberTextField
                  {...TF}
                  label={"Primary Auto Market Limit ($)"}
                  value={data?.primaryAutoMarketLimit ?? null}
                  onChangeNumber={(v) => onUpdate("primaryAutoMarketLimit", v)}
                />
              </Grid>
            </Grid>
          </Stack>
        </Stack>
      </Collapse>
    </Stack>
  );
};

const AutoPricing = () => {
  const dispatch = useDispatch();
  const data = useSelector(programSelectors.getAutoExposureRating);
  const persisted = useSelector(programSelectors.getAutoPricingResult);
  const submission = useSelector(
    submissionSelector.selectResolvedSubmissionWithoutInput
  );
  const proRataFactor = submission?.program?.proRataAdjustment
    ? submission.program.proRataAdjustment / 100
    : 1;
  const { data: config } = staticDataService.useConfigQuery(
    "auto_exposure_rating_config"
  );

  const [error, setError] = useState(null);

  const result = persisted?.values ?? null;
  const selectedRates = persisted?.selectedRates ?? {};

  const [
    _triggerPricing,
    { isFetching },
  ] = analyticsService.useLazySubmissionAnalyticsQuery();

  const handleUpdate = useCallback(
    (field, value) =>
      dispatch(programActions.updateAutoExposureRating(field, value)),
    [dispatch]
  );

  const handleUpdateRate = useCallback(
    (key, value) =>
      dispatch(
        programActions.setAutoPricingResult({
          values: persisted?.values,
          selectedRates: { ...(persisted?.selectedRates ?? {}), [key]: value },
        })
      ),
    [dispatch, persisted]
  );

  const handleCalculate = async () => {
    setError(null);
    try {
      const response = await _triggerPricing({
        engine: ENGINE,
        metric: "program_pricing",
        submission,
      });
      if (response?.data?.failure) {
        setError(response.data.failure);
        dispatch(programActions.setAutoPricingResult(null));
      } else {
        dispatch(
          programActions.setAutoPricingResult({
            values: response?.data?.values ?? null,
            // Keep the UW's selected-rate overrides across a recalculate.
            selectedRates: persisted?.selectedRates ?? {},
          })
        );
        dispatch(programActions.setXsRatingAutoCalculate(true));
      }
    } catch (err) {
      setError(err?.message ?? "Pricing request failed.");
      dispatch(programActions.setAutoPricingResult(null));
    }
  };

  return (
    <Disabled ifReadOnly>
      <Component title={"Auto Pricing"} isFetching={isFetching}>
        <Stack direction={"column"} gap={2} sx={{ pr: 2 }}>
          <ExposureRatingInputs
            data={data}
            config={config}
            onUpdate={handleUpdate}
          />
          <Stack
            direction={"row"}
            justifyContent={"flex-end"}
            alignItems={"center"}
            spacing={2}
          >
            {error && (
              <Alert severity={"error"} sx={{ width: "100%" }}>
                {error}
              </Alert>
            )}
            <Button onClick={handleCalculate} isLoading={isFetching}>
              {"Calculate"}
            </Button>
          </Stack>
          {result && (
            <>
              <Divider />
              <AutoPricingResults
                values={result}
                selectedRates={selectedRates}
                onUpdateRate={handleUpdateRate}
                data={data}
                onUpdate={handleUpdate}
                proRataFactor={proRataFactor}
              />
            </>
          )}
        </Stack>
      </Component>
    </Disabled>
  );
};

export default AutoPricing;
